import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ActivationJourneyService,
  ActivationJourneyState,
  ActivationStepId,
  activationStepToSlug,
  ResumeDestination,
  shouldSkipAnnounce,
  shouldSkipFairSetup,
  trustFirstCoreBeforeInviteDone,
} from './activation-journey.service';
import { OnboardingChecklistService } from './onboarding-checklist.service';
import {
  ActivationEvidenceSnapshot,
  ActivationReadinessResult,
  ActivationReadinessService,
  emptyEvidenceSnapshot,
  evaluateActivationReadiness,
} from './activation-readiness.service';
import { sysConfig } from '../sysConfig';
import { TokenEncryptionService } from '../Services/token-encryption.service';

export type ExampleProminence = 'hidden' | 'supporting' | 'prominent';

export interface ActivationDestination {
  route: string;
  queryParams?: Params;
  reason: string;
  ctaLabel: string;
  resumeDestination: ResumeDestination;
  exampleProminence: ExampleProminence;
  /** When a requested deep link was invalid, explain the degradation. */
  degradedFrom?: string | null;
}

export interface ResolveDestinationInput {
  state: ActivationJourneyState;
  evidence?: ActivationEvidenceSnapshot | null;
  readiness?: ActivationReadinessResult | null;
  /** Optional path the founder tried to open (e.g. /setup/review). */
  requestedPath?: string | null;
  /**
   * Intentional re-open (profile Journey menu). Allows /setup/ready even when
   * the journey is completed or skipped so existing customers can explore / restart.
   */
  allowReentry?: boolean;
}

const SETUP_STEP_PATHS: Record<
  string,
  | ActivationStepId
  | 'ready'
  | 'understand'
  | 'purpose'
  | 'collection'
  | 'access'
  | 'announce'
  | 'install'
  | 'screenshot'
  | 'confirm'
  | 'example-workday'
> = {
  '/setup': 'ready',
  '/setup/ready': 'ready',
  '/setup/purpose': 'plan_viewed',
  '/setup/understand': 'plan_viewed',
  '/setup/collection': 'collection',
  '/setup/prepare': 'mode_education',
  '/setup/install': 'install',
  '/setup/screenshot': 'screenshot',
  '/setup/access': 'setup_confirmed',
  '/setup/confirm': 'setup_confirmed',
  '/setup/announce': 'announce',
  '/setup/first-employee': 'first_employee',
  '/setup/review': 'first_day_review',
  '/setup/example-workday': 'example-workday',
};

function normalizePath(path: string | null | undefined): string {
  if (!path) {
    return '';
  }
  const bare = path.split('?')[0].replace(/\/+$/, '') || '/';
  return bare;
}

/** Funnel order for intentional menu/restart re-walks (slug paths under /setup). */
const REENTRY_WALK_ORDER = [
  '/setup/ready',
  '/setup/purpose',
  '/setup/understand',
  '/setup/collection',
  '/setup/access',
  '/setup/confirm',
  '/setup/announce',
  '/setup/prepare',
  '/setup/install',
  '/setup/screenshot',
  '/setup/first-employee',
  '/setup/review',
] as const;

function reentryWalkRank(path: string): number {
  const idx = REENTRY_WALK_ORDER.indexOf(path as (typeof REENTRY_WALK_ORDER)[number]);
  return idx >= 0 ? idx : -1;
}

/**
 * Completed/skipped + allowReentry: stay on the step the founder is walking.
 * Never bounce to Dashboard, and never let leftover hire/private-test evidence
 * resolveActive-hijack Collection beat navigation.
 */
function resolveIntentionalReentryWalk(
  state: ActivationJourneyState,
  requested: string
): ActivationDestination {
  const currentPath = `/setup/${activationStepToSlug(state.currentStep)}`;
  const resumeForStep = (path: string): ResumeDestination => {
    if (path === '/setup/collection') return 'collection';
    if (path === '/setup/purpose' || path === '/setup/understand') return 'purpose';
    if (path === '/setup/ready' || path === '/setup') return 'ready';
    return null;
  };

  if (!requested || requested === '/setup') {
    return destination({
      route: currentPath,
      reason: 'Continue the current step during intentional Journey re-entry',
      ctaLabel: 'Continue setup',
      resumeDestination: resumeForStep(currentPath),
      exampleProminence: currentPath === '/setup/ready' ? 'prominent' : 'supporting',
    });
  }

  // Current step including query-only beat changes (?beat=mode).
  if (requested === currentPath) {
    return destination({
      route: requested,
      reason: 'Continue the current step during intentional Journey re-entry',
      ctaLabel: 'Continue setup',
      resumeDestination: resumeForStep(requested),
      exampleProminence: 'supporting',
    });
  }

  const requestedRank = reentryWalkRank(requested);
  const currentRank = reentryWalkRank(currentPath);
  // Browser back / earlier funnel pages are fine; future deep links snap to current.
  if (requestedRank >= 0 && currentRank >= 0 && requestedRank <= currentRank) {
    return destination({
      route: requested,
      reason: 'Revisit an earlier setup step during intentional Journey re-entry',
      ctaLabel: 'Continue setup',
      resumeDestination: resumeForStep(requested),
      exampleProminence: requested === '/setup/ready' ? 'prominent' : 'supporting',
    });
  }

  if (requested.startsWith('/setup')) {
    return destination({
      route: currentPath,
      reason: 'That page isn’t available on this walkthrough yet — staying on your current step.',
      ctaLabel: 'Continue setup',
      resumeDestination: resumeForStep(currentPath),
      exampleProminence: 'supporting',
      degradedFrom: requested,
    });
  }

  return destination({
    route: currentPath,
    reason: 'Continue the current step during intentional Journey re-entry',
    ctaLabel: 'Continue setup',
    resumeDestination: resumeForStep(currentPath),
    exampleProminence: 'supporting',
  });
}

function destination(
  partial: Omit<ActivationDestination, 'exampleProminence'> & {
    exampleProminence?: ExampleProminence;
  }
): ActivationDestination {
  return {
    exampleProminence: partial.exampleProminence ?? 'supporting',
    degradedFrom: partial.degradedFrom ?? null,
    queryParams: partial.queryParams,
    route: partial.route,
    reason: partial.reason,
    ctaLabel: partial.ctaLabel,
    resumeDestination: partial.resumeDestination,
  };
}

/**
 * Pure destination resolution from journey state + readiness evidence.
 * Matches the plan's canonical destination rules.
 */
export function resolveActivationDestination(
  input: ResolveDestinationInput
): ActivationDestination {
  const state = input.state;
  const evidence = input.evidence ?? emptyEvidenceSnapshot();
  const readiness =
    input.readiness ??
    evaluateActivationReadiness(evidence, { hasEvidenceInput: !!input.evidence });

  const requested = normalizePath(input.requestedPath);
  const reentryToReady =
    !!input.allowReentry &&
    (!requested || requested === '/setup' || requested === '/setup/ready');
  const terminalCompleted = state.journeyStatus === 'completed' || state.finished;
  const terminalSkipped = state.journeyStatus === 'skipped' || state.skipped;
  // Menu / Restart: keep completion history, but let the founder re-walk /setup.
  // Must NOT fall through to resolveActive — leftover firstEmployeeId / private-test
  // evidence would hijack Collection back to invite/explore and freeze Continue.
  const intentionalReentryWalk = !!input.allowReentry && (terminalCompleted || terminalSkipped);

  if (terminalCompleted || terminalSkipped) {
    if (reentryToReady) {
      return destination({
        route: '/setup/ready',
        reason: terminalCompleted
          ? 'Reopened Journey from menu after completion'
          : 'Reopened Journey from menu after skip',
        ctaLabel: 'Explore Journey',
        resumeDestination: 'ready',
        exampleProminence: 'prominent',
      });
    }
    if (intentionalReentryWalk) {
      return resolveIntentionalReentryWalk(state, requested);
    }
    return destination({
      route: '/dashboard',
      reason: terminalCompleted ? 'Journey completed' : 'Journey skipped',
      ctaLabel: 'Open dashboard',
      resumeDestination: null,
      exampleProminence: 'hidden',
      // Bare Back into /setup after finish must degrade (menu re-entry uses allowReentry).
      degradedFrom: requested.startsWith('/setup') ? requested : null,
    });
  }

  // Paused: use stored resume destination / step — never generic dashboard.
  if (state.journeyStatus === 'paused') {
    return resolveFromResumeKey(
      state.resumeDestination || inferResumeKey(state),
      state,
      readiness,
      'Paused — resume where you left off'
    );
  }

  const resolved = resolveActive(state, readiness, evidence);
  if (!requested || requested === '/setup' || !requested.startsWith('/setup')) {
    return resolved;
  }

  // Deep-link validation: degrade future/invalid setup URLs to nearest valid destination.
  if (!isRequestedPathAllowed(requested, resolved, state, readiness)) {
    return {
      ...resolved,
      degradedFrom: requested,
      reason: `${resolved.reason}. That page isn’t available yet — opening the next valid step.`,
    };
  }

  return resolved;
}

function inferResumeKey(state: ActivationJourneyState): ResumeDestination {
  if (state.evaluationStatus === 'private_active' || state.privateEvaluationActive) {
    return 'explore';
  }
  if (state.inviteFirstFromReady && !state.privateTestEmployeeId) {
    if (state.firstEmployeeId) {
      return 'first_employee';
    }
    return shouldSkipFairSetup(state) ? 'first_employee' : 'confirm';
  }
  switch (state.pausedAtStep || state.currentStep) {
    case 'goal_selected':
      return 'ready';
    case 'plan_viewed':
      return 'purpose';
    case 'collection':
      return 'collection';
    case 'mode_education':
    case 'private_test':
      return 'install';
    case 'private_proof':
      return 'screenshot';
    case 'setup_confirmed':
      return 'access';
    case 'announce':
      return 'announce';
    case 'first_employee':
      return 'first_employee';
    case 'first_day_review':
      return 'review';
    default:
      return 'ready';
  }
}

/** Trust-first path before optional private test / invite. */
function resolveTrustFirstBeforeInvite(state: ActivationJourneyState): ActivationDestination | null {
  const done = new Set(state.completedSteps || []);
  const step = state.currentStep;

  // Invite-first shortcut already on first_employee — do not bounce to Purpose/Collection.
  if (
    state.inviteFirstFromReady &&
    state.wantsEmployeeInvite &&
    step === 'first_employee' &&
    !state.firstEmployeeId
  ) {
    return null;
  }

  if (step === 'goal_selected' || !done.has('goal_selected')) {
    return destination({
      route: '/setup/ready',
      reason: 'Workspace Ready',
      ctaLabel: 'Continue',
      resumeDestination: 'ready',
      exampleProminence: 'prominent',
    });
  }

  if (step === 'plan_viewed' || !done.has('plan_viewed')) {
    return destination({
      route: '/setup/purpose',
      reason: 'Purpose — why introduce SuperSee',
      ctaLabel: 'Continue',
      resumeDestination: 'purpose',
      exampleProminence: 'prominent',
    });
  }

  if (step === 'collection' || !done.has('collection')) {
    return destination({
      route: '/setup/collection',
      reason: 'Collection settings for the pilot',
      ctaLabel: 'Save and continue',
      resumeDestination: 'collection',
      exampleProminence: 'supporting',
    });
  }

  // Team note and access are only on the invite branch, after the founder chooses it.
  if (state.wantsEmployeeInvite && step === 'announce' && !shouldSkipAnnounce(state)) {
    return destination({
      route: '/setup/announce',
      reason: 'Announce — tell the team before the invite',
      ctaLabel: 'Continue',
      resumeDestination: 'announce',
      exampleProminence: 'supporting',
    });
  }

  if (state.wantsEmployeeInvite && step === 'setup_confirmed' && !shouldSkipFairSetup(state)) {
    return destination({
      route: '/setup/access',
      reason: 'Access — who can see the team',
      ctaLabel: 'Continue',
      resumeDestination: 'access',
      exampleProminence: 'supporting',
    });
  }

  return null;
}

function resolveActive(
  state: ActivationJourneyState,
  readiness: ActivationReadinessResult,
  evidence: ActivationEvidenceSnapshot
): ActivationDestination {
  if (state.evaluationStatus === 'private_active' || state.privateEvaluationActive) {
    return exploreDestination(state, 'Exploring private-test data');
  }

  const hasPrivateId = !!state.privateTestEmployeeId;
  const step = state.currentStep;
  const completed = new Set(state.completedSteps || []);

  // Real hire already started — never bounce back into Purpose/Collection.
  // Exception: after Collection, currentStep is first_employee with no hire id → Preview fork.
  if (
    (state.firstEmployeeId || completed.has('first_employee') || step === 'first_day_review') &&
    !(step === 'first_employee' && !state.firstEmployeeId && !completed.has('first_employee'))
  ) {
    return resolveEmployeePhase(state, readiness);
  }

  // Optional private-test install nest (from Invite) — only when already on install steps.
  if (!hasPrivateId && (step === 'private_test' || step === 'mode_education')) {
    return destination({
      route: '/setup/install',
      reason: 'Optional private test — download when ready',
      ctaLabel: 'Download SuperSee',
      resumeDestination: 'install',
      exampleProminence: 'supporting',
    });
  }

  // Trust-first path when there is no private-test identity yet.
  if (!hasPrivateId) {
    const trustFirst = resolveTrustFirstBeforeInvite(state);
    if (trustFirst) {
      return trustFirst;
    }
    return destination({
      route: '/setup/first-employee',
      reason: 'Invite the pilot',
      ctaLabel: 'Add employee',
      resumeDestination: 'first_employee',
      exampleProminence: 'supporting',
    });
  }

  // Private-test nest only: identity without heartbeat → install wait.
  // Do not hijack Collection → Preview (first_employee) with leftover privateTestEmployeeId
  // or the default activationStatusPhase=package_prepared.
  if (!readiness.agentOnline && !evidence.recentHeartbeat && !evidence.agentAuthenticated) {
    if (step === 'private_test' || step === 'mode_education') {
      return destination({
        route: '/setup/install',
        reason: 'Private identity exists but agent is not connected',
        ctaLabel: 'Finish connecting SuperSee',
        resumeDestination: 'install',
        exampleProminence: 'supporting',
      });
    }
  }

  // Heartbeat but no screenshot → capture — only while still in the private-test nest.
  if (
    (step === 'private_test' || step === 'mode_education' || step === 'private_proof') &&
    (readiness.agentOnline || evidence.recentHeartbeat || evidence.agentAuthenticated) &&
    !readiness.screenshotReady &&
    !completed.has('private_proof') &&
    state.activationStatusPhase !== 'first_data'
  ) {
    return destination({
      route: '/setup/screenshot',
      reason: 'Connected — waiting for first screenshot',
      ctaLabel: 'Take my first screenshot',
      resumeDestination: 'screenshot',
      exampleProminence: 'supporting',
    });
  }

  // Screenshot / first_data / private_proof completed — sticky fork or employee path.
  // Explore only when privateEvaluationActive (handled above). Do not default to explore
  // merely because a screenshot exists while still on the private_proof fork.
  if (
    readiness.screenshotReady ||
    state.activationStatusPhase === 'first_data' ||
    completed.has('private_proof')
  ) {
    if (step === 'first_employee' || completed.has('first_employee') || state.firstEmployeeId) {
      return resolveEmployeePhase(state, readiness);
    }
    if (step === 'announce') {
      if (shouldSkipAnnounce(state)) {
        return destination({
          route: '/setup/first-employee',
          reason: 'Announce complete — invite the pilot',
          ctaLabel: 'Add employee',
          resumeDestination: 'first_employee',
          exampleProminence: 'supporting',
        });
      }
      return destination({
        route: '/setup/announce',
        reason: 'Announce — tell the team before install',
        ctaLabel: 'Continue',
        resumeDestination: 'announce',
        exampleProminence: 'supporting',
      });
    }
    if (step === 'setup_confirmed') {
      if (shouldSkipFairSetup(state)) {
        if (shouldSkipAnnounce(state)) {
          return destination({
            route: '/setup/first-employee',
            reason: 'Access complete — invite the pilot',
            ctaLabel: 'Add employee',
            resumeDestination: 'first_employee',
            exampleProminence: 'supporting',
          });
        }
        return destination({
          route: '/setup/announce',
          reason: 'Announce — tell the team before install',
          ctaLabel: 'Continue',
          resumeDestination: 'announce',
          exampleProminence: 'supporting',
        });
      }
      return destination({
        route: '/setup/access',
        reason: 'Access — who can see the team',
        ctaLabel: 'Continue',
        resumeDestination: 'access',
        exampleProminence: 'supporting',
      });
    }
    if (step === 'collection') {
      return destination({
        route: '/setup/collection',
        reason: 'Collection settings for the pilot',
        ctaLabel: 'Save and continue',
        resumeDestination: 'collection',
        exampleProminence: 'supporting',
      });
    }
    if (step === 'plan_viewed') {
      return destination({
        route: '/setup/purpose',
        reason: 'Purpose — why introduce SuperSee',
        ctaLabel: 'Continue',
        resumeDestination: 'purpose',
        exampleProminence: 'prominent',
      });
    }
    // Sticky fork: still on private_proof without choosing explore → stay on screenshot.
    if (step === 'private_proof') {
      return destination({
        route: '/setup/screenshot',
        reason: 'Screenshot ready — choose explore or try with one employee',
        ctaLabel: 'Continue with your screenshot',
        resumeDestination: 'screenshot',
        exampleProminence: 'supporting',
      });
    }
    // Stale early currentStep (e.g. goal_selected) must not erase a completed screenshot.
    return destination({
      route: '/setup/screenshot',
      reason: 'Screenshot ready — choose explore or try with one employee',
      ctaLabel: 'Continue with your screenshot',
      resumeDestination: 'screenshot',
      exampleProminence: 'supporting',
    });
  }

  // Fallback by current step.
  return resolveFromResumeKey(inferResumeKey(state), state, readiness, 'Continue setup');
}

function resolveEmployeePhase(
  state: ActivationJourneyState,
  readiness: ActivationReadinessResult
): ActivationDestination {
  if (!state.firstEmployeeId) {
    // Legacy private-proof path (or trust-first complete): land on invite.
    if (shouldSkipFairSetup(state) && shouldSkipAnnounce(state)) {
      return destination({
        route: '/setup/first-employee',
        reason: 'Add the first employee',
        ctaLabel: 'Add employee',
        resumeDestination: 'first_employee',
      });
    }
    const trustFirst = resolveTrustFirstBeforeInvite(state);
    if (trustFirst) {
      return trustFirst;
    }
    return destination({
      route: '/setup/first-employee',
      reason: 'Add the first employee',
      ctaLabel: 'Add employee',
      resumeDestination: 'first_employee',
    });
  }

  if (!readiness.agentOnline) {
    return destination({
      route: '/setup/first-employee',
      reason: 'Waiting for employee agent connection',
      ctaLabel: 'Finish connecting',
      resumeDestination: 'first_employee',
    });
  }

  if (readiness.earlyReviewReady) {
    return destination({
      route: '/setup/review',
      reason: 'Early review ready',
      ctaLabel: 'Review first-day signals',
      resumeDestination: 'review',
      exampleProminence: 'hidden',
    });
  }

  // Agent online but early review not ready — stay collecting on first-employee, not review.
  return destination({
    route: '/setup/first-employee',
    reason:
      'SuperSee is working on their computer. Return to check whether the first review is ready.',
    ctaLabel: 'Check first signals',
    resumeDestination: 'first_employee',
    exampleProminence: 'supporting',
  });
}

function exploreDestination(state: ActivationJourneyState, reason: string): ActivationDestination {
  const employeeId = state.privateTestEmployeeId;
  return destination({
    route: '/screenshots',
    queryParams: employeeId ? { employeeId: String(employeeId) } : undefined,
    reason,
    ctaLabel: 'Continue reviewing this computer',
    resumeDestination: 'explore',
    exampleProminence: 'supporting',
  });
}

function resolveFromResumeKey(
  key: ResumeDestination,
  state: ActivationJourneyState,
  readiness: ActivationReadinessResult,
  reason: string
): ActivationDestination {
  switch (key) {
    case 'ready':
      return destination({
        route: '/setup/ready',
        reason,
        ctaLabel: 'Continue setup',
        resumeDestination: 'ready',
        exampleProminence: 'prominent',
      });
    case 'understand':
    case 'purpose':
      return destination({
        route: '/setup/purpose',
        reason,
        ctaLabel: 'Continue',
        resumeDestination: 'purpose',
        exampleProminence: 'prominent',
      });
    case 'collection':
      return destination({
        route: '/setup/collection',
        reason,
        ctaLabel: 'Save and continue',
        resumeDestination: 'collection',
      });
    case 'access':
      return destination({
        route: '/setup/access',
        reason,
        ctaLabel: 'Continue',
        resumeDestination: 'access',
      });
    case 'announce':
      return destination({
        route: '/setup/announce',
        reason,
        ctaLabel: 'Continue',
        resumeDestination: 'announce',
      });
    case 'install':
      return destination({
        route: '/setup/install',
        reason,
        ctaLabel: 'Finish connecting SuperSee',
        resumeDestination: 'install',
      });
    case 'screenshot':
      return destination({
        route: '/setup/screenshot',
        reason,
        ctaLabel: 'Take my first screenshot',
        resumeDestination: 'screenshot',
      });
    case 'explore':
      return exploreDestination(state, reason);
    case 'confirm':
      return destination({
        route: '/setup/access',
        reason,
        ctaLabel: 'Continue',
        resumeDestination: 'access',
      });
    case 'first_employee':
      return destination({
        route: '/setup/first-employee',
        reason,
        ctaLabel: 'Check employee connection',
        resumeDestination: 'first_employee',
      });
    case 'review':
      if (!readiness.earlyReviewReady) {
        return destination({
          route: '/setup/first-employee',
          reason:
            'SuperSee is working on their computer. Return to check whether the first review is ready.',
          ctaLabel: 'Check first signals',
          resumeDestination: 'first_employee',
          exampleProminence: 'supporting',
        });
      }
      return destination({
        route: '/setup/review',
        reason,
        ctaLabel: 'Review first-day signals',
        resumeDestination: 'review',
      });
    default:
      return destination({
        route: '/setup/ready',
        reason,
        ctaLabel: 'Continue setup',
        resumeDestination: 'ready',
        exampleProminence: 'prominent',
      });
  }
}

function isRequestedPathAllowed(
  requested: string,
  resolved: ActivationDestination,
  state: ActivationJourneyState,
  readiness: ActivationReadinessResult
): boolean {
  if (requested === normalizePath(resolved.route)) {
    return true;
  }
  if (
    state.inviteFirstFromReady &&
    !state.privateTestEmployeeId &&
    (requested === '/setup/confirm' ||
      requested === '/setup/access' ||
      requested === '/setup/announce' ||
      requested === '/setup/first-employee')
  ) {
    return true;
  }
  // Allow example workday from ready/purpose/explore contexts.
  if (requested === '/setup/example-workday') {
    return (
      resolved.resumeDestination === 'ready' ||
      resolved.resumeDestination === 'understand' ||
      resolved.resumeDestination === 'purpose' ||
      resolved.resumeDestination === 'explore' ||
      resolved.resumeDestination === 'screenshot' ||
      state.evaluationStatus === 'private_active' ||
      state.currentStep === 'plan_viewed'
    );
  }

  const mapped = SETUP_STEP_PATHS[requested];
  if (!mapped) {
    // Unknown /setup/* paths are not open holes — degrade to resolved destination.
    if (requested.startsWith('/setup')) {
      return false;
    }
    return true;
  }

  const order: Array<string> = [
    'ready',
    'purpose',
    'collection',
    'access',
    'announce',
    'install',
    'screenshot',
    'explore',
    'first_employee',
    'review',
  ];
  const requestedKey =
    mapped === 'ready'
      ? 'ready'
      : mapped === 'plan_viewed' || mapped === 'understand' || mapped === 'purpose'
        ? 'purpose'
        : mapped === 'collection'
          ? 'collection'
          : mapped === 'setup_confirmed' || mapped === 'confirm' || mapped === 'access'
            ? 'access'
            : mapped === 'announce'
              ? 'announce'
              : mapped === 'mode_education' || mapped === 'install'
                ? 'install'
                : mapped === 'screenshot'
                  ? 'screenshot'
                  : mapped === 'first_employee'
                    ? 'first_employee'
                    : mapped === 'first_day_review'
                      ? 'review'
                      : null;
  if (!requestedKey) {
    return true;
  }
  const resolvedIdx = order.indexOf(resolved.resumeDestination || 'ready');
  const requestedIdx = order.indexOf(requestedKey);
  // Future steps beyond current resolution are not allowed.
  if (requestedIdx > resolvedIdx) {
    // Exception: review allowed when early review ready even if route says first_employee.
    if (requestedKey === 'review' && readiness.earlyReviewReady) {
      return true;
    }
    return false;
  }
  return true;
}

@Injectable({
  providedIn: 'root',
})
export class ActivationDestinationService {
  constructor(
    private readiness: ActivationReadinessService,
    private activationJourney: ActivationJourneyService,
    private onboardingService: OnboardingChecklistService,
    private tokenEncryptionService: TokenEncryptionService
  ) {}

  resolve(input: Omit<ResolveDestinationInput, 'readiness' | 'evidence'> & {
    evidence?: ActivationEvidenceSnapshot | null;
  }): ActivationDestination {
    const evidence = input.evidence ?? this.readiness.getSnapshot();
    const readiness = this.readiness.evaluate(evidence);
    return resolveActivationDestination({
      ...input,
      evidence,
      readiness,
    });
  }

  /**
   * After email OTP / workspace prompt: Ready when the journey is on and not finished.
   * Paused and invite-first use resolve() so they never dump to dashboard — except a
   * live first-employee connection waiting for activity, which is not unfinished setup.
   */
  postEmailActivationDestination(options?: {
    hasExistingEmployees?: boolean;
  }): ActivationDestination {
    if (!sysConfig.activationJourneyEnabled) {
      return destination({
        route: '/dashboard',
        reason: 'Activation journey off',
        ctaLabel: 'Open dashboard',
        resumeDestination: null,
        exampleProminence: 'hidden',
      });
    }
    const state = this.activationJourney.getState();
    if (
      state.journeyStatus === 'completed' ||
      state.journeyStatus === 'skipped' ||
      state.finished ||
      state.skipped
    ) {
      return destination({
        route: '/dashboard',
        reason: 'Journey completed or skipped',
        ctaLabel: 'Open dashboard',
        resumeDestination: null,
        exampleProminence: 'hidden',
      });
    }
    // Existing customers who already completed classic onboarding (or have
    // employees/screenshots) should not be funnelled into the activation
    // journey on login — send them straight to the dashboard.
    if (this.onboardingService.isPermanentlyCompleted()) {
      return destination({
        route: '/dashboard',
        reason: 'Classic onboarding already completed',
        ctaLabel: 'Open dashboard',
        resumeDestination: null,
        exampleProminence: 'hidden',
      });
    }
    // Fresh journey: new trial signups still go to Ready. Paid accounts, and
    // trial workspaces that already have employees, go to the dashboard.
    // firstEmployeeId is journey-only — employees added from /employees never
    // set it, and logout clears the local completion flag.
    if (this.journeyLooksUntouched(state)) {
      const user =
        this.tokenEncryptionService.getEncryptedItem<any>('user') ||
        this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
      const quotaStatus = user?.meta?.quota_status;
      const isTrial = String(quotaStatus || '').toLowerCase() === 'trial';

      if (!isTrial || options?.hasExistingEmployees) {
        return destination({
          route: '/dashboard',
          reason: options?.hasExistingEmployees
            ? 'Company already has employees — skip private-test setup'
            : 'Journey never started — non-trial existing account',
          ctaLabel: 'Open dashboard',
          resumeDestination: null,
          exampleProminence: 'hidden',
        });
      }
    }
    // Try one hire already exists. This Admin tab only checks status — do not
    // reopen it on every trial login after logout cleared the local cache.
    if (state.firstEmployeeId) {
      return destination({
        route: '/dashboard',
        reason: 'First employee already added — collecting in background',
        ctaLabel: 'Open dashboard',
        resumeDestination: null,
        exampleProminence: 'hidden',
      });
    }
    return this.resolve({ state, requestedPath: '/setup' });
  }

  /**
   * Fresh login clears local journey cache. Hydrate from the server before
   * choosing Ready vs dashboard, otherwise trial accounts look brand-new.
   * When the hydrated journey is still untouched, also check whether the
   * company already has employees — trial is not a proxy for "never set up".
   */
  postLoginDestination(): Observable<ActivationDestination> {
    if (!sysConfig.activationJourneyEnabled) {
      return of(this.postEmailActivationDestination());
    }
    return this.activationJourney.hydrateFromServer().pipe(
      switchMap(() => {
        const state = this.activationJourney.getState();
        if (!this.journeyLooksUntouched(state)) {
          return of(this.postEmailActivationDestination());
        }
        return this.onboardingService.hasExistingEmployees().pipe(
          map((hasExistingEmployees) =>
            this.postEmailActivationDestination({ hasExistingEmployees })
          ),
          catchError(() => of(this.postEmailActivationDestination()))
        );
      })
    );
  }

  private journeyLooksUntouched(state: ActivationJourneyState): boolean {
    return (
      state.completedSteps.length === 0 &&
      !state.goal &&
      !state.modeEducationSeen &&
      !state.privateTestEmployeeId &&
      !state.firstEmployeeId
    );
  }

  /**
   * After Continue later: they asked to leave the funnel — put them in the real admin.
   * Do not wall them on /setup (including Example workday). The resume bar is the
   * invitation back; it is not a gate.
   *
   * Prefer a page that already has their data. If they have none yet, Dashboard is
   * the product front door (sidebar, header, Journey) — not an empty Employees table,
   * and not another setup room.
   */
  resolveContinueLaterLanding(state: ActivationJourneyState): ActivationDestination {
    const dest = this.resolve({ state });
    if (!dest.route.startsWith('/setup') && dest.route !== '/dashboard') {
      return dest;
    }

    const hasRealHire =
      !!state.firstEmployeeId && state.firstEmployeeId !== state.privateTestEmployeeId;
    const hasPrivateProof =
      (state.completedSteps || []).includes('private_proof') ||
      state.activationStatusPhase === 'first_data' ||
      state.privateEvaluationActive ||
      state.evaluationStatus === 'private_active';

    if (hasRealHire) {
      return destination({
        route: '/employees',
        reason: 'Continue later — real admin, their hire is here',
        ctaLabel: dest.ctaLabel,
        resumeDestination: dest.resumeDestination,
        exampleProminence: 'supporting',
      });
    }

    if (hasPrivateProof && state.privateTestEmployeeId) {
      return destination({
        route: '/screenshots',
        queryParams: { employeeId: String(state.privateTestEmployeeId) },
        reason: 'Continue later — real admin, their private proof is here',
        ctaLabel: dest.ctaLabel,
        resumeDestination: dest.resumeDestination,
        exampleProminence: 'supporting',
      });
    }

    return destination({
      route: '/dashboard',
      reason: 'Continue later — real admin, setup stays optional via resume bar',
      ctaLabel: dest.ctaLabel,
      resumeDestination: dest.resumeDestination,
      exampleProminence: 'supporting',
    });
  }

  resolveResume(state: ActivationJourneyState): ActivationDestination {
    return this.resolve({ state });
  }
}
